export const flowBodyFromFormData = <T>(formData: FormData): T =>
  Object.fromEntries(formData.entries()) as T;

/** Ory identifies verification resend requests with the clicked submit control named `email`. */
export const verificationBodyFromFormData = <T>(formData: FormData): T => {
  const verificationFormData = new FormData();
  const isResend = formData.has('email');

  for (const [name, value] of formData.entries()) {
    if (isResend && name === 'code') continue;
    verificationFormData.append(name, value);
  }

  return flowBodyFromFormData<T>(verificationFormData);
};

const normalizeRecoveryAddress = (value: string): string => {
  if (value.includes('@')) return value;

  const digits = value.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return value;
};

/**
 * Normalize recovery address confirmations to the E.164 value stored on the identity. RecoveryV2
 * selection values remain opaque and unchanged. Resend and Back are alternative actions, so
 * browser autofill must not turn either into a challenge submission.
 */
export const recoveryBodyFromFormData = <T>(formData: FormData): T => {
  const normalizedFormData = new FormData();
  const isAuxiliaryAction =
    formData.has('recovery_confirm_address') || formData.get('screen') === 'previous';

  for (const [name, value] of formData.entries()) {
    if (name === 'code' && isAuxiliaryAction) continue;

    if (
      (name === 'recovery_address' || name === 'recovery_confirm_address') &&
      typeof value === 'string'
    ) {
      normalizedFormData.append(name, normalizeRecoveryAddress(value));
    } else {
      normalizedFormData.append(name, value);
    }
  }

  return flowBodyFromFormData<T>(normalizedFormData);
};

export const addSubmitterToFormData = (
  formData: FormData,
  submitter: HTMLElement | { name?: string; value?: string } | null
): FormData => {
  const control = submitter as { name?: string; value?: string } | null;
  if (control?.name) {
    formData.set(control.name, control.value ?? '');
  }

  return formData;
};

export const getOryBrowserRedirect = (responseData: unknown): string | null => {
  if (!responseData || typeof responseData !== 'object') return null;

  const response = responseData as {
    redirect_browser_to?: unknown;
    continue_with?: unknown;
  };
  if (typeof response.redirect_browser_to === 'string' && response.redirect_browser_to.length > 0) {
    return response.redirect_browser_to;
  }

  if (!Array.isArray(response.continue_with)) return null;
  const continuation = response.continue_with.find(
    (step) =>
      typeof step === 'object' &&
      step !== null &&
      (step as Record<string, unknown>).action === 'redirect_browser_to' &&
      typeof (step as Record<string, unknown>).redirect_browser_to === 'string'
  ) as Record<string, unknown> | undefined;
  return typeof continuation?.redirect_browser_to === 'string'
    ? continuation.redirect_browser_to
    : null;
};

/**
 * Return the browser destination Ory supplies after a recovery challenge succeeds.
 * A successful recovery does not finish on the recovery form: it normally continues
 * into the privileged settings flow where the user can choose a new password.
 */
export const getOryRecoveryContinuationRedirect = (continuations: unknown): string | null => {
  if (!Array.isArray(continuations)) return null;

  for (const continuation of continuations) {
    if (typeof continuation !== 'object' || continuation === null) continue;
    const record = continuation as Record<string, unknown>;

    if (record.action === 'show_settings_ui') {
      if (typeof record.flow !== 'object' || record.flow === null) continue;
      const url = (record.flow as Record<string, unknown>).url;
      if (typeof url === 'string' && url.length > 0) return url;
    }

    if (record.action === 'redirect_browser_to') {
      const redirect = record.redirect_browser_to;
      if (typeof redirect === 'string' && redirect.length > 0) return redirect;
    }
  }

  return null;
};

export const getOryVerificationFlowIdFromUrl = (url: string): string | undefined => {
  const redirect = new URL(url, globalThis.location?.origin ?? 'http://localhost');
  if (!redirect.pathname.endsWith('/verification')) return undefined;
  return redirect.searchParams.get('flow') ?? undefined;
};

export const getOryVerificationFlowId = (continuations: unknown): string | undefined => {
  if (!Array.isArray(continuations)) return undefined;

  for (const continuation of continuations) {
    if (typeof continuation !== 'object' || continuation === null) continue;
    const record = continuation as Record<string, unknown>;
    if (record.action === 'show_verification_ui') {
      if (typeof record.flow !== 'object' || record.flow === null) continue;
      const flow = record.flow as Record<string, unknown>;
      if (typeof flow.id === 'string' && flow.id.length > 0) return flow.id;
      if (typeof flow.url === 'string') {
        const flowId = getOryVerificationFlowIdFromUrl(flow.url);
        if (flowId) return flowId;
      }
    }

    if (record.action === 'redirect_browser_to' && typeof record.redirect_browser_to === 'string') {
      const flowId = getOryVerificationFlowIdFromUrl(record.redirect_browser_to);
      if (flowId) return flowId;
    }
  }

  return undefined;
};

/** Select only the address-specific settings continuation; never fall back to another address. */
export const getOryVerificationContinuationUrl = (
  continuations: unknown,
  verifiableAddress: string
): string | undefined => {
  if (!Array.isArray(continuations)) return undefined;
  for (const continuation of continuations) {
    if (typeof continuation !== 'object' || continuation === null) continue;
    const record = continuation as Record<string, unknown>;
    if (record.action !== 'show_verification_ui' || !record.flow || typeof record.flow !== 'object')
      continue;
    const flow = record.flow as Record<string, unknown>;
    if (flow.verifiable_address !== verifiableAddress || typeof flow.url !== 'string') continue;
    if (getOryVerificationFlowIdFromUrl(flow.url)) return flow.url;
  }
  return undefined;
};

export const getUnverifiedSmsAddress = (addresses: unknown): string | undefined => {
  if (!Array.isArray(addresses)) return undefined;

  for (const address of addresses) {
    if (typeof address !== 'object' || address === null) continue;
    const record = address as Record<string, unknown>;
    if (record.via !== 'sms' || record.verified === true) continue;
    if (typeof record.value === 'string' && record.value.length > 0) return record.value;
  }

  return undefined;
};

export const applyMfaDeliveryMethodToRedirect = (
  redirect: string,
  traits: Record<string, unknown>,
  verifiableAddresses: unknown = []
): string => {
  const preferredMethod =
    traits.mfa_delivery_method === 'sms' || traits.mfa_delivery_method === 'email'
      ? traits.mfa_delivery_method
      : 'email';
  const redirectUrl = new URL(redirect);
  const phoneTrait = Object.entries(traits).find(
    ([name, value]) => /phone|mobile/i.test(name) && typeof value === 'string'
  )?.[1];
  const phone = typeof phoneTrait === 'string' ? normalizeRecoveryAddress(phoneTrait) : '';
  const hasSmsVerificationAddress =
    Array.isArray(verifiableAddresses) &&
    verifiableAddresses.some((address) => {
      if (typeof address !== 'object' || address === null) return false;
      const record = address as Record<string, unknown>;
      return (
        record.via === 'sms' &&
        record.verified === true &&
        typeof record.value === 'string' &&
        normalizeRecoveryAddress(record.value) === phone
      );
    });
  // Ory's deprecated `via` parameter expects the identity trait name, not the courier name.
  // A legacy identity may contain a phone trait without that trait being registered as an SMS
  // verification address. Sending `via=phone` for such an identity makes Ory reject the entire
  // login. Fall back to the registered email credential until SMS enrollment exists.
  redirectUrl.searchParams.set(
    'via',
    preferredMethod === 'sms' && hasSmsVerificationAddress ? 'phone' : 'email'
  );
  return redirectUrl.toString();
};

export type MfaDeliveryMethod = 'email' | 'sms';

export const getPersistedMfaDeliveryMethod = (
  traits: Record<string, unknown>,
  cachedMethod: MfaDeliveryMethod | null
): MfaDeliveryMethod => {
  if (traits.mfa_delivery_method === 'email' || traits.mfa_delivery_method === 'sms') {
    return traits.mfa_delivery_method;
  }

  // The cache supports identity schemas that cannot store mfa_delivery_method. In particular,
  // sms_consent and application notification settings are not delivery preferences.
  return cachedMethod ?? 'email';
};

export const isSmsMfaAvailable = (
  traits: Record<string, unknown>,
  verifiableAddresses: unknown,
  effectivelyConsented: boolean
): boolean => {
  if (!effectivelyConsented || !Array.isArray(verifiableAddresses)) return false;

  const phoneTrait = Object.entries(traits).find(
    ([name, value]) => /phone|mobile/i.test(name) && typeof value === 'string'
  )?.[1];
  if (typeof phoneTrait !== 'string' || phoneTrait.length === 0) return false;
  const phone = normalizeRecoveryAddress(phoneTrait);

  return verifiableAddresses.some((address) => {
    if (typeof address !== 'object' || address === null) return false;
    const record = address as Record<string, unknown>;
    return (
      record.via === 'sms' &&
      record.verified === true &&
      typeof record.value === 'string' &&
      normalizeRecoveryAddress(record.value) === phone
    );
  });
};

export const getOryMfaVia = (method: MfaDeliveryMethod): 'email' | 'phone' =>
  method === 'sms' ? 'phone' : 'email';

const MFA_PREFERENCE_KEY_PREFIX = 'ory_mfa_delivery_method:';

export const cacheMfaDeliveryMethod = (
  identifier: string,
  method: MfaDeliveryMethod,
  storage: Pick<Storage, 'setItem'> = localStorage
): void => {
  storage.setItem(`${MFA_PREFERENCE_KEY_PREFIX}${identifier.trim().toLowerCase()}`, method);
};

export const getCachedMfaDeliveryMethod = (
  identifier: string,
  storage: Pick<Storage, 'getItem'> = localStorage
): MfaDeliveryMethod | null => {
  const method = storage.getItem(`${MFA_PREFERENCE_KEY_PREFIX}${identifier.trim().toLowerCase()}`);
  return method === 'email' || method === 'sms' ? method : null;
};

export const clearCachedMfaDeliveryMethod = (
  identifier: string,
  storage: Pick<Storage, 'removeItem'> = localStorage
): void => {
  storage.removeItem(`${MFA_PREFERENCE_KEY_PREFIX}${identifier.trim().toLowerCase()}`);
};
